const FORM_SELECTOR = 'form[data-recaptcha-form]';
const TOKEN_FIELD = 'recaptcha_token';
const ACTION_FIELD = 'recaptcha_action';
const RESUME_FLAG = 'recaptchaResumeSubmit';

/**
 * Reads the localized public reCAPTCHA settings exposed by WordPress.
 */
const getSettings = () => window.cbiRecaptchaSettings || {};

/**
 * Returns a translated/localized message with a hardcoded fallback.
 */
const getMessage = (key, fallback) => {
  const value = getSettings()[key];
  return typeof value === 'string' && value ? value : fallback;
};

/**
 * Resolves the Enterprise API object after Google's script has loaded.
 */
const getRecaptcha = () => window.grecaptcha?.enterprise || null;

/**
 * Creates or updates a hidden field without reusing old token values.
 */
const setHiddenInput = (form, name, value) => {
  let input = form.querySelector(`input[name="${name}"]`);

  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    form.appendChild(input);
  }

  input.value = value;
};

/**
 * Marks the protected form as busy while a token is being generated.
 */
const setBusy = (form, busy) => {
  form.setAttribute('aria-busy', busy ? 'true' : 'false');
};

/**
 * Clears the form-level reCAPTCHA error area, when the template provides one.
 */
const clearError = (form) => {
  const error = form.querySelector('[data-recaptcha-error]');

  if (!error) {
    return;
  }

  error.textContent = '';
  error.classList.add('d-none');
};

/**
 * Shows a blocking reCAPTCHA error without silently submitting the form.
 */
const showError = (form, message) => {
  const error = form.querySelector('[data-recaptcha-error]');

  if (error) {
    error.textContent = message;
    error.classList.remove('d-none');
    return;
  }

  form.dispatchEvent(
    new CustomEvent('cbi:recaptcha:error', {
      detail: { message },
    })
  );
};

/**
 * Requests a fresh score-based Enterprise token for the current form action.
 */
const executeRecaptcha = (action) =>
  new Promise((resolve, reject) => {
    const { siteKey } = getSettings();
    const recaptcha = getRecaptcha();

    if (!siteKey || !recaptcha?.ready || !recaptcha?.execute) {
      reject(new Error(getMessage('loadingError', 'The verification service is not available. Please try again later.')));
      return;
    }

    recaptcha.ready(() => {
      recaptcha
        .execute(siteKey, { action })
        .then(resolve)
        .catch(() => {
          reject(new Error(getMessage('errorMessage', 'We could not verify your submission. Please try again.')));
        });
    });
  });

/**
 * Replays the original submit after the token is injected, avoiding loops.
 */
const resumeSubmit = (form, submitter) => {
  form.dataset[RESUME_FLAG] = 'true';
  const validSubmitter = submitter && form.contains(submitter) ? submitter : undefined;

  if (typeof form.requestSubmit === 'function') {
    form.requestSubmit(validSubmitter);
    return;
  }

  const event = new Event('submit', {
    bubbles: true,
    cancelable: true,
  });

  form.dispatchEvent(event);

  if (!event.defaultPrevented && typeof form.submit === 'function') {
    form.submit();
  }
};

/**
 * Captures protected form submits before AJAX handlers run, then resumes them.
 */
const handleSubmit = async (event) => {
  const form = event.target?.closest?.(FORM_SELECTOR);

  if (!form) {
    return;
  }

  if (form.dataset[RESUME_FLAG] === 'true') {
    delete form.dataset[RESUME_FLAG];
    clearError(form);
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();

  if (!form.checkValidity()) {
    form.classList.add('was-validated');
    form.reportValidity?.();
    return;
  }

  const action = form.getAttribute('data-recaptcha-action') || '';

  if (!action) {
    showError(form, getMessage('errorMessage', 'We could not verify your submission. Please try again.'));
    return;
  }

  clearError(form);
  setBusy(form, true);

  try {
    const token = await executeRecaptcha(action);

    setHiddenInput(form, TOKEN_FIELD, token);
    setHiddenInput(form, ACTION_FIELD, action);
    resumeSubmit(form, event.submitter || document.activeElement);
  } catch (error) {
    showError(form, error.message || getMessage('errorMessage', 'We could not verify your submission. Please try again.'));
  } finally {
    setBusy(form, false);
  }
};

/**
 * Binds reCAPTCHA only when at least one explicit opt-in form is present.
 */
const initRecaptchaForms = () => {
  if (!document.querySelector(FORM_SELECTOR)) {
    return;
  }

  document.addEventListener('submit', handleSubmit, true);
};

document.addEventListener('DOMContentLoaded', initRecaptchaForms);
